import type { Delivery, DeliveryDriver, DeliveryStatus } from '@erp/shared';
import { ArrowLeft, MapPin, Upload } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { Badge, Card, Skeleton } from '../../components/shadcn/index.js';
import { Button, EmptyState } from '../../components/ui/index.js';
import { fetchAttachmentObjectUrl } from '../../lib/attachments.js';
import { assignDelivery, getDelivery, listDeliveryDrivers, setDeliveryStatus, uploadDeliveryProof } from '../../lib/delivery.js';
import { formatDateTime } from '../../lib/intl.js';
import { formatMoney } from '../../lib/money.js';

const TONE: Record<DeliveryStatus, 'secondary' | 'info' | 'warning' | 'success' | 'danger'> = {
  pending: 'secondary', assigned: 'info', out_for_delivery: 'warning', delivered: 'success', failed: 'danger',
};
const NEXT_STATUSES: DeliveryStatus[] = ['assigned', 'out_for_delivery', 'delivered', 'failed'];
const STATUS_KEY: Record<DeliveryStatus, string> = {
  assigned: 'detail.markAssigned', out_for_delivery: 'detail.markOut', delivered: 'detail.markDelivered', failed: 'detail.markFailed', pending: 'status.pending',
};
const PHOTO_MIME = new Set(['image/png', 'image/jpeg', 'image/webp', 'image/gif']);
const PHOTO_MAX = 8 * 1024 * 1024;

export function DeliveryDetailPage(): JSX.Element {
  const { t, i18n } = useTranslation('delivery');
  const { id = '' } = useParams();
  const [d, setD] = useState<Delivery | null>(null);
  const [state, setState] = useState<'loading' | 'ok' | 'notfound'>('loading');
  const [drivers, setDrivers] = useState<DeliveryDriver[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setState('loading');
    getDelivery(id).then((v) => { setD(v); setState('ok'); }).catch(() => setState('notfound'));
    listDeliveryDrivers().then(setDrivers).catch(() => setDrivers([]));
  }, [id]);

  const money = (v: string | null): string => (v && d?.currencyCode ? formatMoney(v, { locale: i18n.language, currency: d.currencyCode }) : '—');

  const onAssign = async (driverUserId: string): Promise<void> => {
    setSaving(true);
    try {
      setD(await assignDelivery(id, driverUserId || null));
      toast.success(t('actions.assigned'));
    } catch { toast.error(t('actions.failed')); } finally { setSaving(false); }
  };

  const onStatus = async (status: DeliveryStatus): Promise<void> => {
    const failureReason = status === 'failed' ? (window.prompt(t('detail.failureReason')) ?? undefined) : undefined;
    setSaving(true);
    try {
      setD(await setDeliveryStatus(id, { status, failureReason }));
      toast.success(t('actions.statusUpdated'));
    } catch { toast.error(t('actions.failed')); } finally { setSaving(false); }
  };

  if (state === 'loading') return <Card className="p-6"><Skeleton className="h-72 w-full" /></Card>;
  if (state === 'notfound' || !d) return <EmptyState title={t('detail.notFound')} />;

  const mapsUrl = d.latitude && d.longitude ? `https://maps.google.com/?q=${d.latitude},${d.longitude}` : null;

  return (
    <div className="flex flex-col gap-4">
      <Link to="/delivery" className="inline-flex w-fit items-center gap-1 text-sm text-fg-muted hover:text-fg">
        <ArrowLeft className="h-4 w-4" aria-hidden />{t('detail.back')}
      </Link>

      <header className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <h1 className="font-mono text-2xl font-semibold">{d.docNumber}</h1>
          <Badge variant={TONE[d.status]}>{t(`status.${d.status}`)}</Badge>
        </div>
      </header>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {/* Recipient */}
        <Card className="flex flex-col gap-1 p-4 text-sm">
          <h2 className="mb-1 text-xs font-semibold uppercase tracking-wide text-fg-muted">{t('detail.recipientCard')}</h2>
          <div className="font-medium">{d.recipientName ?? '—'}</div>
          {d.phone && <a href={`tel:${d.phone}`} className="text-brand hover:underline">{d.phone}</a>}
          {d.addressLine1 || d.city ? (
            <div className="text-fg-muted">
              {d.addressLine1 && <div>{d.addressLine1}</div>}
              {d.addressLine2 && <div>{d.addressLine2}</div>}
              {d.city && <div>{d.city}</div>}
            </div>
          ) : <div className="text-fg-muted">{t('detail.noAddress')}</div>}
          {mapsUrl && (
            <a href={mapsUrl} target="_blank" rel="noopener noreferrer" className="mt-1 inline-flex items-center gap-1 text-brand hover:underline">
              <MapPin className="h-3.5 w-3.5" aria-hidden />{t('my.openMap')}
            </a>
          )}
        </Card>

        {/* Order */}
        <Card className="flex flex-col gap-1 p-4 text-sm">
          <h2 className="mb-1 text-xs font-semibold uppercase tracking-wide text-fg-muted">{t('detail.orderCard')}</h2>
          <div className="font-mono">{d.sourceNumber || '—'}</div>
          <div className="flex justify-between"><span className="text-fg-muted">{t('board.total')}</span><span className="font-mono tabular-nums">{money(d.orderTotal)}</span></div>
          {d.deliveredAt && <div className="text-fg-muted">{t('detail.deliveredAt')}: {formatDateTime(d.deliveredAt, { locale: i18n.language })}</div>}
          {d.failureReason && <div className="text-danger">{t('detail.failureReason')}: {d.failureReason}</div>}
        </Card>

        {/* Driver + status */}
        <Card className="flex flex-col gap-3 p-4 text-sm">
          <div>
            <h2 className="mb-1 text-xs font-semibold uppercase tracking-wide text-fg-muted">{t('detail.driverCard')}</h2>
            {drivers.length === 0 ? (
              <p className="text-xs text-fg-muted">{t('detail.noDrivers')}</p>
            ) : (
              <select
                value={d.driverUserId ?? ''}
                disabled={saving}
                onChange={(e) => void onAssign(e.target.value)}
                className="block h-9 w-full rounded-md border border-border bg-bg px-2 text-sm text-fg outline-none focus:border-brand focus:ring-2 focus:ring-brand/20"
              >
                <option value="">{t('detail.selectDriver')}</option>
                {drivers.map((dr) => <option key={dr.id} value={dr.id}>{dr.fullName}</option>)}
              </select>
            )}
          </div>
          <div>
            <h2 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-fg-muted">{t('detail.statusCard')}</h2>
            <div className="flex flex-wrap gap-1.5">
              {NEXT_STATUSES.map((s) => (
                <Button key={s} size="sm" variant={s === 'delivered' ? undefined : 'secondary'} disabled={saving || s === d.status} onClick={() => void onStatus(s)}>
                  {t(STATUS_KEY[s])}
                </Button>
              ))}
            </div>
          </div>
        </Card>
      </div>

      <ProofCard delivery={d} onUploaded={setD} />
    </div>
  );
}

/** Proof-of-delivery photo: preview (authed object URL) + upload/replace. */
function ProofCard({ delivery, onUploaded }: { delivery: Delivery; onUploaded: (d: Delivery) => void }): JSX.Element {
  const { t } = useTranslation('delivery');
  const inputRef = useRef<HTMLInputElement>(null);
  const objUrlRef = useRef<string | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const show = useCallback((next: string | null): void => {
    if (objUrlRef.current) URL.revokeObjectURL(objUrlRef.current);
    objUrlRef.current = next;
    setPreview(next);
  }, []);

  useEffect(() => {
    let active = true;
    const att = delivery.proofAttachmentId;
    if (!att) { show(null); return undefined; }
    void fetchAttachmentObjectUrl(att).then((u) => { if (active) show(u); else URL.revokeObjectURL(u); }).catch(() => { if (active) show(null); });
    return () => { active = false; };
  }, [delivery.proofAttachmentId, show]);

  useEffect(() => () => { if (objUrlRef.current) URL.revokeObjectURL(objUrlRef.current); }, []);

  const onFile = async (file: File): Promise<void> => {
    if (!PHOTO_MIME.has(file.type) || file.size > PHOTO_MAX) { toast.error(t('actions.badPhoto')); return; }
    setBusy(true);
    try {
      onUploaded(await uploadDeliveryProof(delivery.id, file, 'manage'));
      show(URL.createObjectURL(file));
      toast.success(t('actions.proofUploaded'));
    } catch { toast.error(t('actions.failed')); } finally { setBusy(false); }
  };

  return (
    <Card className="flex flex-col gap-3 p-4">
      <h2 className="text-xs font-semibold uppercase tracking-wide text-fg-muted">{t('detail.proofCard')}</h2>
      <div className="flex items-start gap-4">
        <div className="aspect-square w-32 flex-none overflow-hidden rounded-md border border-border bg-bg-subtle">
          {preview ? <img src={preview} alt="" className="h-full w-full object-cover" /> : <div className="grid h-full w-full place-items-center text-xs text-fg-muted">{t('detail.noProof')}</div>}
        </div>
        <div>
          <input ref={inputRef} type="file" accept={[...PHOTO_MIME].join(',')} className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) void onFile(f); if (e.target) e.target.value = ''; }} />
          <Button variant="secondary" size="sm" loading={busy} onClick={() => inputRef.current?.click()}>
            <Upload className="h-4 w-4" aria-hidden />{preview ? t('detail.replaceProof') : t('detail.uploadProof')}
          </Button>
        </div>
      </div>
    </Card>
  );
}
